/**
 * tags.ts — order tag presets (thẻ đơn hàng).
 *
 * Tags live on the order itself (`order.tags: string[]`, BE column `orders.tags`)
 * — set at creation and edited on the detail page, filtered server-side via the
 * `tag` query param. This module owns the preset catalogue, the label lookup
 * and the pre-shift warn-tag rule; there is no settings/config layer for order
 * tags anymore.
 *
 * The Schedule view reads the same array (carried on `ScheduleRow.tags`), so a
 * label set on the order detail page shows on the calendar card and vice versa.
 */

export type TagPreset = { label: string; color: string; bg: string };

/**
 * Hai thẻ ê-kíp tick ngay trong hộp "Công việc" của ca chụp. Nhãn PHẢI trùng
 * từng ký tự với `ekipschedule.TagSuaCanThan` / `TagDacBiet` bên backend và
 * với khoá của máy chia việc sửa hình — sai một dấu là chip mất màu và máy
 * chia bỏ qua thẻ mà không lỗi nào nổi lên.
 */
export const TAG_SUA_CAN_THAN = "🧡 Sửa cẩn thận";
export const TAG_DAC_BIET = "🟣 Đặc biệt";

/**
 * Warn tags (QĐ 0128): the order tags a photographer or makeup artist must see
 * before a shift, shown as the "Lưu ý ca này" strip on the shift card. 🧡 Sửa
 * cẩn thận is deliberately not one: it concerns retouch, not the shoot.
 *
 * Mirrors `orderdispatch.WarnTags` on the backend, which writes the same names
 * into the assigned / offer / tomorrow messages. Both sides run the shared cases
 * in backend/internal/core/orderdispatch/testdata/warn_tags_cases.json, so
 * changing the rule on one side turns the other side's test red.
 */
export const TAG_GAP = "🔴 Gấp";
/** KOL is an order flag (`orders.is_kol`), not a preset; a stray tag with this label still counts. */
export const TAG_KOL = "⭐ KOL";
/** Display order of the warn tags, whatever order they were added in. */
export const WARN_TAGS: readonly string[] = [TAG_GAP, TAG_DAC_BIET, TAG_KOL];

/**
 * TAG_PRESETS — the fixed catalogue of order labels. Stored by `label` (emoji
 * preserved) so BE containment queries and FE badges share one key.
 *
 * ĐỪNG thêm lại preset "🟠 Tạm hoãn" (gỡ 03/09/2026, CEO chốt). Nó là một nhãn
 * DÁN TAY, trong khi "tạm hoãn" thật là một trạng thái máy ghi ở
 * `settings.postponed_orders` — hai thứ khác hẳn nhau mang cùng một cái tên.
 * Đo trên dữ liệu thật lúc gỡ: 0/6360 đơn đeo nhãn đó (thực ra 0 đơn nào đeo
 * BẤT KỲ nhãn nào), nên chip chỉ tổ đứng cạnh thẻ lọc thật ở màn Đơn hàng và
 * cạnh nút "⏸ Tạm hoãn vô thời hạn" ở màn Lịch mà không lọc ra gì — bấm vào là
 * danh sách trống, người dùng đọc thành "không có đơn hoãn nào".
 *
 * Đơn cũ (nếu có) vẫn giữ nguyên chuỗi trong `orders.tags`: `tagStyle` trả chip
 * trung tính cho nhãn ngoài catalogue, và OrderTagsSection xếp nó vào nhóm nhãn
 * tự do gỡ được — gỡ preset KHÔNG xoá chữ của ai.
 */
export const TAG_PRESETS: TagPreset[] = [
  { label: TAG_GAP, color: "#B74949", bg: "#FFF0F0" },
  { label: TAG_DAC_BIET, color: "#7B5AAE", bg: "#F3EEFA" },
  // Nhãn KHÓ của khâu sửa hình. Không chỉ để nhìn: máy chia đơn đọc nhãn
  // này rồi chỉ giao cho người mà cơ sở đã chỉ định. Chuỗi nhãn CHÍNH LÀ
  // khoá trong danh mục `order_tags` (seed ở migration 0221) — lệch một ký
  // tự là hai nhãn khác nhau và cổng chỉ định im lặng hụt. Đổi chữ ở đây
  // thì phải đổi cả dòng seed, và ngược lại.
  { label: TAG_SUA_CAN_THAN, color: "#8B4513", bg: "#FBEFE6" },
];

/** Look up a preset by its label. Returns undefined for unknown labels. */
export function presetByLabel(label: string | null | undefined): TagPreset | undefined {
  if (!label) return undefined;
  return TAG_PRESETS.find((p) => p.label === label);
}

/** Neutral chip colours for a label outside the preset catalogue. */
const CUSTOM_TAG_STYLE: Omit<TagPreset, "label"> = {
  color: "var(--color-text-secondary)",
  bg: "var(--color-surface-3)",
};

/**
 * Chip colours for any tag label. Presets keep their own palette; free-text
 * labels (the "ghi chú tùy chỉnh" box on the schedule panel writes those) get a
 * neutral chip rather than disappearing — the caller must be able to render
 * every value stored on the order.
 */
export function tagStyle(label: string): TagPreset {
  return presetByLabel(label) ?? { label, ...CUSTOM_TAG_STYLE };
}

/** Toggle a tag label in a tag array, returning a new array. */
export function toggleTag(tags: string[], label: string): string[] {
  return tags.includes(label) ? tags.filter((t) => t !== label) : [...tags, label];
}

/** Hover text on a tag the viewer may not take off: removing is a manager's job (CEO 03/09/2026). */
export const TAG_REMOVE_LOCK_HINT = "Chỉ quản lý mới gỡ được nhãn";
/** Hover text on a tag the viewer's role may not put on — no `order.tag.add` (CEO 24/09/2026). */
export const TAG_ADD_LOCK_HINT = "Vai của bạn không được gắn nhãn";

/**
 * Why a preset tag button is locked for this viewer, or undefined when it is
 * not. One button adds (tag off) and removes (tag on), and the server guards
 * the two with separate permissions (order/handler.go), so each state locks on
 * its own. The order page and the schedule panel both call this — the panel
 * once kept its own copy and let every role press ✕ into a 403.
 */
export function presetTagLock(active: boolean, canAdd: boolean, canRemove: boolean): string | undefined {
  if (active) return canRemove ? undefined : TAG_REMOVE_LOCK_HINT;
  return canAdd ? undefined : TAG_ADD_LOCK_HINT;
}

// Matching is on the WHOLE normalised tag, so free text such as "Không gấp"
// or "gấp đôi ảnh" never counts as the Gấp tag.
const WARN_TAG_ALIASES = new Map<string, string>([
  ["gấp", TAG_GAP],
  ["gap", TAG_GAP],
  ["đặc biệt", TAG_DAC_BIET],
  ["dac biet", TAG_DAC_BIET],
  ["kol", TAG_KOL],
]);

/**
 * Normalises a tag the way `orderdispatch.warnTagKey` does: NFC first (macOS
 * keyboards emit decomposed Vietnamese), lower case, keep only letters, digits
 * and white space (drops the emoji), collapse the white space. \p{White_Space}
 * is exactly Go's unicode.IsSpace; \s is not.
 */
function warnTagKey(tag: string): string {
  return tag
    .normalize("NFC")
    .toLowerCase()
    .replace(/[^\p{L}\p{Nd}\p{White_Space}]/gu, "")
    .split(/\p{White_Space}+/u)
    .filter(Boolean)
    .join(" ");
}

/**
 * The warn tags an order carries, in WARN_TAGS order and without duplicates.
 * `isKol` always counts as KOL, whatever the tags say.
 */
export function warnTags(tags: readonly string[] | null | undefined, isKol: boolean): string[] {
  const hit = new Set<string>();
  if (isKol) hit.add(TAG_KOL);
  for (const tag of tags ?? []) {
    const label = WARN_TAG_ALIASES.get(warnTagKey(tag));
    if (label) hit.add(label);
  }
  return WARN_TAGS.filter((label) => hit.has(label));
}

/**
 * KOL chip: white on the darker cut of the KOL pink that the schedule card
 * already uses for KOL text (scheduleColors.ts). The brand pink #E91E63 only
 * reaches 4.35:1 behind white; this reaches about 5.9:1.
 */
const KOL_CHIP_STYLE: TagPreset = { label: TAG_KOL, color: "#FFFFFF", bg: "#c2185b" };

/** Chip colours for a warn tag: the preset palette, plus KOL's own chip. */
export function warnTagStyle(label: string): TagPreset {
  return label === TAG_KOL ? KOL_CHIP_STYLE : tagStyle(label);
}
